/*
 * Placeholder portraits for sample people (stock photos in public/images/avatars).
 * avatarPhoto(seed) always gives the same person the same photo.
 */
export const AVATAR_PHOTOS = Array.from({ length: 8 }, (_, index) => `/images/avatars/avatar-${index + 1}.jpg`)

const hashOf = (seed: string | number) => {
    const text = String(seed)
    let hash = 0
    for (let index = 0; index < text.length; index++) hash = (hash * 31 + text.charCodeAt(index)) | 0

    return Math.abs(hash)
}

export const avatarPhoto = (seed: string | number) => AVATAR_PHOTOS[hashOf(seed) % AVATAR_PHOTOS.length]

/*
 * Supporter portraits (public/images/people, 400 × 500 face crops, about 30 KB each), split by how a first name
 * usually reads so a photo fits the person.
 */
export type PersonPhotoKind = 'woman' | 'man'

export const PEOPLE_PHOTOS: Record<PersonPhotoKind, string[]> = {
    woman: Array.from({ length: 9 }, (_, index) => `/images/people/woman-${index + 1}.jpg`),
    man: Array.from({ length: 9 }, (_, index) => `/images/people/man-${index + 1}.jpg`)
}

const WOMEN = [
    'Abigail', 'Aisha', 'Alana', 'Amara', 'Amber', 'Amy', 'Angela', 'Ava', 'Bianca', 'Brooke', 'Camila', 'Carolyn',
    'Cassandra', 'CeCe', 'Charlotte', 'Chloe', 'Claire', 'Diana', 'Elena', 'Elisa', 'Elizabeth', 'Emily', 'Erica',
    'Esme', 'Fatima', 'Fiona', 'Gloria', 'Grace', 'Gwen', 'Hana', 'Hannah', 'Imani', 'Iris', 'Jade', 'Jasmine',
    'Jenna', 'Jessica', 'Julia', 'Keisha', 'Kristin', 'Laila', 'Leah', 'Lisa', 'Lucy', 'Maya', 'Mei', 'Michelle',
    'Mila', 'Nadia', 'Nancy', 'Nia', 'Nora', 'Olivia', 'Paige', 'Priya', 'Rhea', 'Rosa', 'Ruby', 'Sana', 'Sarah',
    'Simone', 'Sofia', 'Talia', 'Tessa', 'Uma', 'Willa', 'Yara', 'Zoe'
]

const MEN = [
    'Adrian', 'Aiden', 'Andre', 'Ben', 'Brandon', 'Caleb', 'Carlos', 'Colin', 'Cory', 'Daniel', 'Darius', 'Diego',
    'Eli', 'Eric', 'Ethan', 'Evan', 'Felix', 'Gabriel', 'Hector', 'Henry', 'Ian', 'Isaac', 'Jamal', 'James', 'Jonah',
    'Julian', 'Kevin', 'Liam', 'Lucas', 'Malik', 'Marco', 'Marcus', 'Michael', 'Nathan', 'Noah', 'Omar', 'Owen',
    'Peter', 'Rafael', 'Ryan', 'Samuel', 'Sean', 'Theo', 'Tomas', 'Victor', 'Wes', 'Wyatt', 'Zach'
]

const NAME_KINDS: Record<string, PersonPhotoKind> = Object.fromEntries([
    ...WOMEN.map(name => [name, 'woman'] as const),
    ...MEN.map(name => [name, 'man'] as const)
])

/* How a first name usually reads. Names that read either way (Alex, Jordan, Sam, Riley) return undefined. */
export const photoKindOf = (name: string): PersonPhotoKind | undefined => NAME_KINDS[name.trim().split(/\s+/)[0] ?? '']

/* Share of people who have a photo. Everyone else shows their initials, as real supporters do. */
export const PERSON_PHOTO_SHARE = 0.65

/*
 * personPhoto(seed, name): the photo a supporter shared, or undefined so the initials show. About two in three people
 * have one, always the same for the same seed, from the pool that fits their first name. A name that reads either
 * way never gets a photo, so a portrait never contradicts the name beside it.
 */
export const personPhoto = (seed: string | number, name: string): string | undefined => {
    const kind = photoKindOf(name)
    if (!kind) return undefined

    const hash = hashOf(`photo:${seed}`)
    if (hash % 100 >= PERSON_PHOTO_SHARE * 100) return undefined

    const pool = PEOPLE_PHOTOS[kind]

    return pool[Math.floor(hash / 100) % pool.length]
}
